import { activity_recent, and, desc, eq, type PowerSyncDrizzleDB } from "@listto/powersync";

const DEFAULT_FEED_LIMIT = 200;

/**
 * PLUGIN-KERNEL: the app-specific dimensions the feed can be narrowed by.
 *
 * listto's feed has none — the local SQLite already contains only this
 * account's buckets. Apps whose feed is filterable declare their dimensions
 * here (comandero: `registerSessionId`), and NOTHING else changes: the plugin
 * and `feed-source.ts` are generic over this type.
 */
export type ActivityFeedFilter = Record<never, never>;

export interface ActivityFeedOptions extends Partial<ActivityFeedFilter> {
  limit?: number;
}

/**
 * Local activity feed (PowerSync `activity_recent` bucket, ~90 days).
 * The account's SQLite contains only its buckets (personal scope + shared lists),
 * so no user re-filtering is needed.
 * Canonical order: server_ts DESC (UUIDv7 id as tie-breaker).
 */
export function getActivityFeedQuery(db: PowerSyncDrizzleDB, options: ActivityFeedOptions = {}) {
  return db
    .select()
    .from(activity_recent)
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}

/**
 * Local history for a specific entity (for the detail sheet's "History").
 * Deep history (beyond retention) is requested from the API
 * (`GET /api/activity/entity/:type/:id`).
 */
export function getEntityActivityQuery(
  db: PowerSyncDrizzleDB,
  entityType: string,
  entityId: string,
  options: { limit?: number } = {},
) {
  return db
    .select()
    .from(activity_recent)
    .where(
      and(eq(activity_recent.entity_type, entityType), eq(activity_recent.entity_id, entityId)),
    )
    .orderBy(desc(activity_recent.server_ts), desc(activity_recent.id))
    .limit(options.limit ?? DEFAULT_FEED_LIMIT);
}
